import { useState, useEffect } from 'react';
import { CreditCard, Shield, CheckCircle, Clock, Zap, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Modal, Btn, toast } from './UI';

const DISMISSED_KEY = '_mc_trial_gate_dismissed';
const DEFAULT_PLAN = 'plan_pro';

/**
 * Shown to admins whose tenant has no stripe_customer_id yet.
 * Prompts them to register a card to start/confirm their 14-day free trial.
 * The user CAN dismiss (stored for this session) but the banner keeps nudging them
 * until they complete checkout. We don't hard-block the app — just make it prominent.
 */
export function TrialActivationGate() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISSED_KEY) === '1');

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.billingSubscription();
        if (!cancelled) setSub(data);
      } catch (e) {
        console.error('TrialActivationGate load:', e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  useEffect(() => {
    // Auto-open modal on first mount if user is eligible and hasn't dismissed
    if (!loading && needsActivation && !dismissed) {
      setShowModal(true);
    }
  }, [loading, dismissed, sub]);

  if (!user || user.role !== 'admin' || loading || !sub) return null;

  const hasCustomer = !!sub.subscription?.customer_id;
  const hasSubscription = !!sub.subscription?.subscription_id;
  const stripeReady = !!sub.stripe_configured;

  // Only nag if: admin, tenant without Stripe subscription, and Stripe is actually configured
  const needsActivation = !hasSubscription && stripeReady;

  const activate = async () => {
    setSubmitting(true);
    try {
      const { url } = await api.billingCheckout(DEFAULT_PLAN);
      if (url) window.location.href = url;
    } catch (e) {
      toast.error('Error', e.message || 'No se pudo iniciar el checkout');
      setSubmitting(false);
    }
  };

  const dismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
    setShowModal(false);
  };

  const trialDays = sub.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(sub.trial_ends_at) - new Date()) / 86400000))
    : 14;

  if (!needsActivation) return null;

  return (
    <>
      {/* Persistent banner (always visible when needed, even if modal dismissed) */}
      {!showModal && (
        <div
          role="status"
          style={{
            position: 'relative', zIndex: 20,
            background: 'linear-gradient(90deg, #2557D6, #1E46B4)',
            color: '#fff',
            padding: '10px 16px',
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            fontSize: 13, fontWeight: 600,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <Clock size={16} />
          <span style={{ flex: 1, minWidth: 0 }}>
            Prueba gratuita — {trialDays} día{trialDays === 1 ? '' : 's'}. Añade una tarjeta para activar tu periodo completo (sin cargos hasta el día {trialDays}).
          </span>
          <button
            onClick={() => setShowModal(true)}
            style={{
              background: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.4)',
              color: '#fff', padding: '6px 12px', borderRadius: 8,
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Añadir tarjeta
          </button>
        </div>
      )}

      <Modal open={showModal} onClose={dismiss} size="md" title="Activa tu prueba gratuita de 14 días">
        <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text)' }}>
          <p style={{ marginTop: 0 }}>
            Registra una tarjeta para desbloquear el acceso completo a <strong>MedCover Pro Professional</strong>.
            No se realizará ningún cargo durante los próximos {trialDays} días.
          </p>

          <div style={{ background: 'var(--surface-alt)', borderRadius: 10, padding: 16, margin: '16px 0' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Qué incluye</div>
            {[
              'Gestión de solicitudes y cobertura sanitaria',
              'Portal cliente + portal profesional',
              'Facturación avanzada y tarifas personalizadas',
              'Notificaciones automáticas por email',
              'Hasta 25 usuarios y 20 vehículos',
              'Soporte prioritario durante el trial',
            ].map(feat => (
              <div key={feat} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, fontSize: 13 }}>
                <CheckCircle size={14} style={{ color: 'var(--success)', flexShrink: 0 }} />
                <span>{feat}</span>
              </div>
            ))}
          </div>

          <div style={{
            display: 'flex', gap: 10, alignItems: 'center',
            padding: '10px 14px', background: 'var(--surface-alt)',
            border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8,
          }}>
            <Shield size={18} style={{ color: 'var(--accent)' }} />
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Pago procesado por <strong style={{ color: 'var(--text)' }}>Stripe</strong>. Tu tarjeta queda registrada y validada, pero no se cobra nada hasta que termine la prueba. Puedes cancelar en cualquier momento.
            </div>
          </div>

          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 20 }}>
            Después del trial: <strong style={{ color: 'var(--text)' }}>€149/mes</strong>. Puedes cambiar de plan o cancelar desde Configuración → Suscripción.
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="secondary" onClick={dismiss} disabled={submitting}>Ahora no</Btn>
            <Btn icon={CreditCard} onClick={activate} loading={submitting}>
              Añadir tarjeta y empezar prueba
            </Btn>
          </div>
        </div>
      </Modal>
    </>
  );
}
